<template>
  <div class="w-[100vw] h-[100vh] fixed top-0 left-0 flex justify-center items-center bg-[#0000005a] z-[9999]" v-if="show">
    <div class="text-[var(--text)] gap-4 p-6 bg-[var(--fundoApp)] rounded-[10px] flex flex-col min-w-[500px] max-w-[600px]">
      <!-- Header -->
      <div class="w-full flex gap-2 justify-between items-center">
        <h3 class="text-[var(--titulo)] text-lg font-semibold">Editar Preço do Equipamento</h3>
        <div class="flex gap-2 items-center">
          <AppButton 
            v-bind:visibility="true" 
            :font_size="12" 
            color="var(--button)"
            left_icon="fa-solid fa-save" 
            text="Guardar" 
            emitType="select"
            @select="saveChanges" 
          />
          <AppButton 
            style="color: var(--icon);" 
            v-bind:visibility="true" 
            left_icon="fa-regular fa-circle-xmark fa-xl"
            emitType="close" 
            @close="close" 
            :width="23" 
            :height="23" 
          />
        </div>
      </div>

      <!-- Equipment Information -->
      <div class="bg-[var(--fundoForm)] bg-opacity-50 rounded-lg p-4">
        <h4 class="text-[var(--label)] text-sm font-medium mb-3">Informações do Equipamento</h4>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex gap-2">
            <label class="min-w-20 text-[var(--label)]">ID:</label>
            <span class="text-[var(--text)]">{{ itemData.equipamento_id }}</span>
          </div>
          <div class="flex gap-2">
            <label class="min-w-20 text-[var(--label)]">Nome:</label>
            <span class="text-[var(--text)]">{{ itemData.equipamento_nome }}</span>
          </div>
          <div class="flex gap-2">
            <label class="min-w-20 text-[var(--label)]">Status:</label>
            <span 
              :class="{
                'text-green-400': itemData.status === 'ativo',
                'text-red-400': itemData.status === 'inativo',
                'text-gray-400': itemData.status === 'eliminado'
              }"
              class="font-semibold uppercase text-xs"
            >
              {{ itemData.status }}
            </span>
          </div>
        </div>
      </div>

      <!-- Price Editing Section -->
      <div class="bg-[var(--fundoForm)] bg-opacity-50 rounded-lg p-4">
        <h4 class="text-[var(--label)] text-sm font-medium mb-3">Preços</h4>
        <div class="grid grid-cols-1 gap-3">
          
          <!-- Editable Venda Field -->
          <div class="flex flex-col gap-1">
            <label class="text-[var(--label)] text-sm">Preço de Venda {{ currentCurrencySymbol }}:</label>
            <input
              type="number"
              step="0.01"
              :min="itemData.vendaMin || 0"
              v-model="editableData.venda"
              class="text-black text-sm border-1 border-[#ccc] p-2 rounded-[5px] w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              :class="{ 'border-red-500': hasError }"
              placeholder="0.00"
            />
            <span v-if="hasError" class="text-red-400 text-xs">
              O preço de venda deve ser no mínimo {{ formatCurrency(itemData.vendaMin || 0) }}
            </span>
          </div>

          <!-- Read-only fields for reference -->
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div class="flex flex-col gap-1">
              <label class="text-[var(--label)]">Venda Mín {{ currentCurrencySymbol }}:</label>
              <input
                type="text"
                :value="formatCurrency(itemData.vendaMin)"
                class="text-gray-500 bg-gray-100 p-2 rounded-[5px] cursor-not-allowed"
                readonly
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[var(--label)]">Compra Atual {{ currentCurrencySymbol }}:</label>
              <input
                type="text"
                :value="formatCurrency(itemData.compraAtual)"
                class="text-gray-500 bg-gray-100 p-2 rounded-[5px] cursor-not-allowed"
                readonly
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Section -->
      <div class="bg-blue-900 bg-opacity-30 rounded-lg p-3">
        <div class="flex justify-between items-center">
          <span class="text-[var(--label)] text-sm">Novo preço de venda:</span>
          <span class="text-[var(--text)] font-semibold text-lg">{{ formatCurrency(editableData.venda || 0) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/updated/button.vue"
import axios from 'axios'

export default {
  name: 'ItemPrecarioEditPopUp',
  components: {
    AppButton
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    itemData: {
      type: Object,
      required: true
    },
    currentCurrency: {
      type: String,
      default: 'EUR'
    },
    currentCurrencySymbol: {
      type: String,
      default: '€'
    }
  },
  data() {
    return {
      baseURl: process.env.VUE_APP_URL_BASE,
      editableData: {
        venda: 0
      },
      hasError: false
    }
  },
  watch: {
    itemData: {
      handler(newData) {
        if (newData) {
          this.editableData.venda = newData.venda || 0
        }
      },
      immediate: true,
      deep: true
    },
    'editableData.venda'() {
      this.hasError = false
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return `${this.currentCurrencySymbol}0,00`
      }
      
      try {
        const formatted = new Intl.NumberFormat('pt-PT', {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol'
        }).format(value)
        
        return formatted.replace('€', this.currentCurrencySymbol)
      } catch (error) {
        return `${this.currentCurrencySymbol}${value || 0}`
      }
    },

    async saveChanges() {
      // Validate input against vendaMin
      const minValue = this.itemData.vendaMin || 0

      if (!this.editableData.venda || parseFloat(this.editableData.venda) < parseFloat(minValue)) {
        this.hasError = true
        return
      }

      try {
        const updateData = {
          venda: this.editableData.venda
        }

        const response = await axios.put(
          `${this.baseURl}/api/v1/itemprecario/editar/${this.itemData.id}/`,
          updateData
        )

        if (response.data) {
          this.$emit('saved', {
            ...this.itemData,
            venda: this.editableData.venda
          })
          this.close()
        }
      } catch (error) {
        console.error('Error updating ItemPrecario:', error)
        this.$emit('error', error)
      }
    },

    close() {
      this.hasError = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.border-red-500 {
  border-color: #ef4444 !important;
}
</style>